import React from 'react';
import { entries, fromPairs, mapValues, } from 'lodash';
import numeral from 'numeral';
import { format as formatDate, addMonths, eachDayOfInterval, startOfDay, startOfMonth, endOfDay, endOfMonth, } from 'date-fns';
import { collection, collectionGroup, query, where, orderBy, getCountFromServer, getAggregateFromServer, sum as dbSum, count as dbCount, } from 'firebase/firestore';
import { LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, } from 'recharts';
import qs from 'qs';
import { useAsync, } from 'react-use';

import firebase from '../../firebase';
import { colors } from '../../shared/config';
import DateSelector from '../DateSelector';
import QuerySelector from '../QuerySelector';
import AdminPage from '../hocs/AdminPage';

const db = firebase.firestore();

const dateDimensions = {
  day: {
    label: '日次',
    xs: _ => eachDayOfInterval({ start: _.startOn, end: _.endOn, }),
    xLabel: _ => formatDate(_, 'MM/dd'),
    start: _ => startOfDay(_),
    end: _ => endOfDay(_),
  },
  month: {
    label: '月次',
    xs: _ => {
      const days = eachDayOfInterval({ start: _.startOn, end: _.endOn, });
      const seen = new Set();
      return days.filter(_ => {
        const key = formatDate(_, 'yyyy/MM');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    xLabel: _ => formatDate(_, 'yyyy/MM'),
    start: _ => startOfMonth(_),
    end: _ => endOfMonth(_),
  },
};
const dateDimensionOptions = entries(dateDimensions).map(([k, _]) => ({ label: _.label, value: k, }));

const queries = [
  { name: 'usersCount', build: (start, end) => ({ q: query(collection(db, 'users'), where('createdAt', '>=', start), where('createdAt', '<=', end)), agg: { count: dbCount() }, pick: _ => _.count }) },
  { name: 'cumulativeUsersCount', build: (start, end) => ({ q: query(collection(db, 'users'), where('createdAt', '<=', end)), agg: { count: dbCount() }, pick: _ => _.count }) },
  { name: 'allOrdersCount', build: (start, end) => ({ q: query(collection(db, 'orders'), where('orderedAt', '>=', start), where('orderedAt', '<=', end)), agg: { count: dbCount() }, pick: _ => _.count }) },
  { name: 'allOrdersTotalPrice', build: (start, end) => ({ q: query(collection(db, 'orders'), where('orderedAt', '>=', start), where('orderedAt', '<=', end), orderBy('orderedAt'), orderBy('totalPrice')), agg: { v: dbSum('totalPrice') }, pick: _ => _.v }) },
  { name: 'allOrdersTotalUserAmount', build: (start, end) => ({ q: query(collection(db, 'orders'), where('orderedAt', '>=', start), where('orderedAt', '<=', end), orderBy('orderedAt'), orderBy('totalUserAmount')), agg: { v: dbSum('totalUserAmount') }, pick: _ => _.v }) },
  { name: 'cancelledCount', build: (start, end) => ({ q: query(collection(db, 'orders'), where('status', '==', 'cancelled'), where('orderedAt', '>=', start), where('orderedAt', '<=', end)), agg: { count: dbCount() }, pick: _ => _.count }) },
  { name: 'cancelledTotalPrice', build: (start, end) => ({ q: query(collection(db, 'orders'), where('status', '==', 'cancelled'), where('orderedAt', '>=', start), where('orderedAt', '<=', end), orderBy('orderedAt'), orderBy('totalPrice')), agg: { v: dbSum('totalPrice') }, pick: _ => _.v }) },
  { name: 'cancelledTotalUserAmount', build: (start, end) => ({ q: query(collection(db, 'orders'), where('status', '==', 'cancelled'), where('orderedAt', '>=', start), where('orderedAt', '<=', end), orderBy('orderedAt'), orderBy('totalUserAmount')), agg: { v: dbSum('totalUserAmount') }, pick: _ => _.v }) },
  { name: 'productsCount', build: (start, end) => ({ q: query(collectionGroup(db, 'products'), where('createdAt', '>=', start), where('createdAt', '<=', end)), agg: { count: dbCount() }, pick: _ => _.count }) },
];

const fetchMetricsForPoint = async (start, end) => {
  const results = await Promise.all(
    queries.map(async ({ name, build }) => {
      const { q, agg, pick } = build(start, end);
      try {
        const data = (await getAggregateFromServer(q, agg)).data();
        return { name, value: pick(data) };
      } catch (e) {
        return { name, error: e.message };
      }
    })
  );

  const errors = results.filter(_ => _.error);
  if (errors.length > 0) {
    throw new Error(errors.map(_ => `[${_.name}] ${_.error}`).join('\n'));
  }

  const r = fromPairs(results.map(_ => [_.name, _.value]));
  const sales = r.allOrdersTotalPrice - r.cancelledTotalPrice;
  const netSales = r.allOrdersTotalUserAmount - r.cancelledTotalUserAmount;

  return {
    usersCount: r.usersCount,
    cumulativeUsersCount: r.cumulativeUsersCount,
    ordersCount: r.allOrdersCount,
    sales,
    userSales: r.cumulativeUsersCount > 0 ? sales / r.cumulativeUsersCount : 0,
    netSales,
    profit: sales - netSales,
    cancelsCount: r.cancelledCount,
    cancelsAmount: r.cancelledTotalPrice,
    productsCount: r.productsCount,
    userProductsCount: r.cumulativeUsersCount > 0 ? r.productsCount / r.cumulativeUsersCount : 0,
  };
};

const metricDefinitions = {
  usersCount: { label: 'ユーザー登録数', },
  cumulativeUsersCount: { label: 'ユーザー数', },
  ordersCount: { label: '販売件数', },
  sales: { label: '売上高（千円）', unit: 'thousandYen', },
  userSales: { label: 'ユーザーあたりの平均売上高（千円）', unit: 'thousandYen', },
  netSales: { label: '総卸値（千円）', unit: 'thousandYen', },
  profit: { label: '粗利（千円）', unit: 'thousandYen', },
  cancelsCount: { label: 'キャンセル数', },
  cancelsAmount: { label: 'キャンセル金額（千円）', unit: 'thousandYen', },
  productsCount: { label: '商品登録数', },
  userProductsCount: { label: 'ユーザーあたりの商品登録数', },
};

export default AdminPage(function AdminDashboard (props) {
  const { history, location } = props;
  const queryParams = qs.parse(decodeURI(location.search.slice(1)));
  const {
    startOn: startOnString = formatDate(addMonths(new Date(), -1), 'yyyy-MM-dd'),
    endOn: endOnString = formatDate(new Date(), 'yyyy-MM-dd'),
    dateDimension: dateDimensionKey = 'day',
  } = queryParams;
  const dateDimension = dateDimensions[dateDimensionKey];
  const startOn = new Date(startOnString);
  const endOn = endOfDay(new Date(endOnString));

  const { value: data, loading: isLoading, error } = useAsync(async () => {
    const xs = dateDimension.xs({ startOn, endOn });
    const thousandYenKeys = entries(metricDefinitions).filter(([, v]) => v.unit === 'thousandYen').map(([k]) => k);
    const results = await Promise.all(
      xs.map(async (x) => {
        const start = dateDimension.start(x);
        const end = dateDimension.end(x);
        const metrics = await fetchMetricsForPoint(start, end);
        return {
          x: dateDimension.xLabel(x),
          ...mapValues(metrics, (v, k) => thousandYenKeys.includes(k) ? Math.round(v / 1000) : v),
        };
      })
    );
    return results;
  }, [startOnString, endOnString, dateDimensionKey]);

  return (
    <div className="admin-users container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>ダッシュボード</h4>
        </div>
        <div className="d-flex justify-content-start mb-3">
          <div className="d-flex align-items-end flex-wrap">
            <QuerySelector paramName="dateDimension" options={dateDimensionOptions} {...{ history, location }} defaultValue="day" />
            <DateSelector className="ml-2" paramName="startOn" label="開始日" history={history} location={location} defaultValue={addMonths(new Date(), -1)} />
            <DateSelector className="ml-2" paramName="endOn" label="終了日" history={history} location={location} defaultValue={new Date()} invalid={startOn > endOn} />
          </div>
        </div>
        {
          error ? (
            <pre className="alert alert-danger" style={{ whiteSpace: 'pre-wrap' }}>{error.message}</pre>
          ) : isLoading ? (
            <span className="fas fa-spin fa-spinner" />
          ) : (
            <div className="mt-4 d-flex flex-wrap justify-content-around">
              {
                entries(metricDefinitions).map(([metric, { label }], i) => {
                  return (
                    <div key={i} className="mt-4">
                      <div className="card p-3">
                        <h4 className="mb-3">{label}</h4>
                        <LineChart width={600} height={300} data={data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="x" />
                          <YAxis tickFormatter={_ => numeral(_).format()} />
                          <Tooltip />
                          <Line type="monotone" dataKey={metric} stroke={colors[i]} strokeWidth={2} />
                        </LineChart>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          )
        }
      </div>
    </div>
  );
});
